import { useEffect, useRef } from 'react';

import useTrackScreenDisplayed from '../useTrackScreenDisplayed/useTrackScreenDisplayed';
import {
  AppTrackingService,
  TrackEventParams,
} from './useTrackScreenDualFire.types';

/**
 * Reports a displayed screen twice, on purpose:
 *
 * 1. the catalogued universal `screen_displayed` with `screen_name` — one event
 *    for every screen in every app, so a funnel can be re-cut without new
 *    instrumentation;
 * 2. the app's legacy `<App>: <Screen>: Opened`, which existing saved Mixpanel
 *    reports and Looker content still read.
 *
 * This dual-fire is the migration (catalog rule `dual-fire`): the legacy event
 * keeps firing until every registered consumer has moved to `screen_displayed`.
 *
 * Pass a nullish `screenName` for a render that is **not** going to display the
 * screen — a route that redirects an ineligible account away, a component
 * mounted before it is known whether it will stay. Both events are then
 * suppressed together: reporting one without the other would make the two series
 * disagree during the very migration they exist to verify, and reporting either
 * inserts a screen the user never saw into every funnel that walks through it.
 *
 * Prefer `createUseTrackScreen` — it binds the service once so a screen only
 * names itself. Call this directly only where the service cannot be reached at
 * module scope, which today means miniapps' per-mini tracking context.
 *
 * On that path both type arguments have to be given explicitly:
 *
 * ```ts
 * useTrackScreenDualFire<CashbackTrackingEvents, 'Cashback: Opened'>(
 *   tracking, 'cashback', 'Cashback: Opened',
 * );
 * ```
 *
 * TypeScript cannot infer `TEvents` from a value whose `trackEvent` is itself
 * generic — it settles on `unknown` and then rejects the event name as `never`,
 * which reads like a wrong event rather than a missing type argument. Relaxing
 * the constraint here does not help; naming the map at the call site does, and
 * it also says which mini's vocabulary is in play.
 */
export default function useTrackScreenDualFire<
  TEvents,
  T extends keyof TEvents & string,
>(
  trackingService: AppTrackingService<TEvents>,
  screenName: string | null | undefined,
  legacyEvent: T | undefined,
  ...params: TrackEventParams<TEvents, T>
) {
  // The screen this hook last reported, NOT a "have we reported yet" boolean.
  // Keying on the same value `useTrackScreenDisplayed` keys on is what makes the
  // two streams fire on identical transitions *by construction*: a lifetime flag
  // would suppress the legacy event on a screen-name change while the universal
  // one re-emitted, and the migration compares those two series.
  //
  // It also arms on the first event actually sent rather than the first render,
  // so a screen that resolves its name asynchronously — or stops redirecting —
  // is still reported rather than silently swallowed.
  const lastReportedRef = useRef<string | null>(null);

  useTrackScreenDisplayed(screenName);

  useEffect(() => {
    // Mirrors `useTrackScreenDisplayed` exactly, including forgetting the screen
    // when it stops being displayed — the two must fire on identical
    // transitions, and a screen left and returned to is a second view.
    if (!screenName) {
      lastReportedRef.current = null;

      return;
    }

    if (lastReportedRef.current === screenName) {
      return;
    }

    lastReportedRef.current = screenName;

    // Absent for a screen that has no legacy counterpart. Apps are not
    // uniformly one or the other: uai reports `ScreenViewed` from its main
    // screen and nothing from its two error screens, and bank-search likewise
    // (FE-2733). The universal event above still fires either way — that is the
    // point of reporting it here rather than per screen.
    if (legacyEvent) {
      trackingService.trackEvent(legacyEvent, ...params);
    }
  }, [screenName, legacyEvent, params, trackingService]);
}
